import { useEffect, ReactNode } from 'react';

import { Outlet } from 'react-router-dom';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { ClickstreamScene } from '@zenbusiness/application-commons-clickstream';
import { PriceListProvider } from '@zenbusiness/application-commons-pricelist';

import useFlowConfig from '#client/hooks/flow/useFlowConfig';
import useFlowState from '#client/hooks/flow/useFlowState';
import { OPTIONS as BUSINESS_EXPERIENCE_OPTIONS } from '#client/pages/BusinessExperience/BusinessExperience';
import { OPTIONS as BUSINESS_STAGE_OPTIONS } from '#client/pages/BusinessStage/BusinessStage';
import { validateEnumValue } from '#client/utils/dataValidation/enumValidation';

import EcommerceProviders from './EcommerceProviders';
import FlowGate from './Flow/FlowGate';
import FlowStateCleaner from './FlowStateCleaner';
import { FlowStateProvider } from './FlowStateProvider';
import LeadManager from './LeadManager';
import { NotificationProvider } from './NotificationManager';
import UserIdentityManager from './UserIdentityManager';

/**
 * NOTE: This is temporary to fix legacy state issues, remove in the future:
 * These fixes only run once at the start of the flow (jh)
 * 1. Validate and fix any invalid business stage/experience values
 * @param {ReactNode} children
 * @returns {ReactNode}
 */
function TempStateFixGate({ children }) {
  const { businessInfo, set } = useFlowState();

  // Validate and fix business info values
  useEffect(() => {
    const { businessStage, businessExperience } = businessInfo;

    const validBusinessStage = validateEnumValue(BUSINESS_STAGE_OPTIONS, businessStage);
    const validBusinessExperience = validateEnumValue(
      BUSINESS_EXPERIENCE_OPTIONS,
      businessExperience
    );

    if (validBusinessStage !== businessStage) {
      set('businessInfo.businessStage', validBusinessStage);
    }
    if (validBusinessExperience !== businessExperience) {
      set('businessInfo.businessExperience', validBusinessExperience);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return children;
}

export default function FlowProviders({ children }) {
  const {
    id: flowId,
    entityType,
    priceListUuid,
    version: flowVersion,
    persistenceGate
  } = useFlowConfig();
  const { authenticated } = useAuthentication();

  const clickstreamSceneProps = {
    flowEntityType: entityType,
    flowId,
    flowVersion: String(flowVersion)
  };

  // The FlowStatePersistenceGate component is designed to persist info into the flow state context.
  // It is placed immediately after FlowStateProvider to ensure that the other
  // providers have the necessary data to function correctly.
  // TODO: TempStateFixGate is a temporary solution to fix legacy state issues
  const FlowStatePersistenceGate = persistenceGate || TempStateFixGate;

  return (
    <ClickstreamScene {...clickstreamSceneProps}>
      <NotificationProvider>
        <PriceListProvider priceListUuid={priceListUuid}>
          <FlowStateCleaner />
          <FlowStateProvider flowId={flowId}>
            <FlowStatePersistenceGate>
              <EcommerceProviders>
                <FlowGate>
                  {authenticated ? <UserIdentityManager /> : <LeadManager />}
                  {children || <Outlet />}
                </FlowGate>
              </EcommerceProviders>
            </FlowStatePersistenceGate>
          </FlowStateProvider>
        </PriceListProvider>
      </NotificationProvider>
    </ClickstreamScene>
  );
}
